import type {RootStore} from "stores/rootStore";
import {
    clearHumanizerDraft,
    consumeRanBatchDraft,
    persistFilesDraft,
    persistHumanizerDraft,
    persistInputDraft,
    persistRanBatchDraft,
    restoreFilesDraft,
    restoreInputDraft,
} from "persistence";
import {consumePostLoginCallback, setPostLoginAction, setPostLoginCallback, type PostLoginAction} from "postLoginAction";

/**
 * Park what is on screen for a login: an OAuth login reloads the page — the app's, or the
 * widget's host page — and `restoreLoginState` brings it back once the account exists.
 * Rewritten by every login, so a prompt the user closed leaves nothing to replay on a later one.
 *
 * `autoRun` — the login continues a scan the user already asked for (the limit auth form):
 * the restore re-runs it; a voluntary login only restores. `humanizerRun` is the same for
 * the Humanize click. `action` / `resume` are the continuation the login was raised for,
 * persisted (survives OAuth) or in memory (survives only an in-place login).
 */
export const persistLoginState = (root: RootStore, {autoRun = false, humanizerRun = false, action = null, resume = null}: {
    autoRun?: boolean;
    humanizerRun?: boolean;
    action?: PostLoginAction | null;
    resume?: (() => void) | null;
} = {}) => {
    const {input, batchFile, humanizer, layout} = root;
    // The result on screen rides along unless the run this login continues replaces it, or
    // it is a batch's, which travels as the ran batch below.
    persistInputDraft(input.text, input.scanModes, autoRun,
        autoRun || batchFile.isBatchMode ? null : input.activeStore.analysisUuid)
    // The files' own tools — the chips, not the tab: restored onto a staged batch they are
    // taken as they are, and the tab's selection would land a deep scan beside the add-on.
    persistFilesDraft(batchFile.getPersistableUploadedFiles(), batchFile.modes, autoRun)
    persistRanBatchDraft({files: autoRun ? [] : batchFile.ranFiles(), modes: batchFile.modes, selected: batchFile.selectedFile})
    // Kept only while the Humanizer is what the user is looking at.
    if (humanizerRun || layout.humanizerOpen) persistHumanizerDraft(humanizer.inputText, humanizer.mode, humanizerRun)
    else clearHumanizerDraft()
    setPostLoginAction(action)
    setPostLoginCallback(resume)
}

/**
 * Re-hydrate what `persistLoginState` parked, once the login has completed. The anon's
 * uploaded files were handed to the account by the backend (connect_anon_with_registered),
 * so they scan by uuid. Files take precedence (a batch is the heavier action); every draft
 * is consumed either way. `pendingAction` is the caller's, already consumed: only its Deep Scan
 * and certificate download are resumed here — the account-level ones stay with the app's handler.
 */
export const restoreLoginState = (root: RootStore, pendingAction: PostLoginAction | null) => {
    const {input, batchFile} = root;
    let restored: Promise<unknown> = Promise.resolve();
    const draftFiles = restoreFilesDraft();
    const draft = restoreInputDraft();
    const ranBatch = consumeRanBatchDraft();
    if (draftFiles?.files.length) {
        batchFile.injectUploadedFiles(draftFiles.files);
        // Modes first: autoRun runs the batch, and it must run in the selection
        // the user staged the files with, not in the default deep scan.
        if (draftFiles.modes) input.applyScanModes(draftFiles.modes);
        if (draftFiles.autoRun) input.makeRunScanRequest();
    } else if (draft?.text && !ranBatch) {
        if (draft.modes) input.applyScanModes(draft.modes);
        restored = input.restoreDraft(draft);
        if (draft.autoRun) input.makeRunScanRequest();
    }
    // A batch that ran comes back with its results, the files' own "log in to unlock".
    // It owns the page, so a text left in the draft must not be restored over it.
    if (ranBatch) restored = batchFile.restoreRan(ranBatch);
    if (pendingAction?.type === 'deep_scan') void restored.then(() => input.runDeepScanForFullAnalysis());
    // Same resume, but by uuid: an OAuth login reloads the page, so the store
    // has to be told which analysis the download was asked for.
    if (pendingAction?.type === 'download_certificate') void root.certificate.download(pendingAction.uuid, pendingAction.surface);

    // Anything else the login was raised BY (an anon's file drop): now that the
    // account exists, run it again. It either goes through — the login WAS the missing
    // permission — or its own handler opens the paywall this account needs.
    consumePostLoginCallback()?.();
}
